import { KatAlert } from "@amzn/katal-react";
import classnames from "classnames";
import Markdown from "markdown-to-jsx";
import React, { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCountrySelection } from "src/components/Contexts/CountrySelectionContext";
import { usePermissions } from "src/components/Contexts/PermissionsContext";
import TranslationKey, {
  DEFAULT_TRANSLATION_CONFIG,
  DEFAULT_TRANSLATION_NS,
} from "src/components/TranslatedField/TranslationKey";
import { normalizeStringForTranslation } from "src/utils";
import styles from "src/styles.module.scss";

const BANNER_VARIANTS: KatAlert.Variant[] = [
  "success",
  "info",
  "warning",
  "danger",
];

interface BannerMatch {
  header: string;
  body: string;
  variant: KatAlert.Variant;
}

const DocumentTypeWarningMessage: FC = () => {
  const { t } = useTranslation(
    DEFAULT_TRANSLATION_NS,
    DEFAULT_TRANSLATION_CONFIG
  );
  const { selectedCountry } = useCountrySelection();
  const { permissions } = usePermissions();

  const countryCode = selectedCountry?.countryCode?.toLowerCase() ?? "";
  const countryPermissions = selectedCountry?.countryCode
    ? permissions.get(selectedCountry.countryCode)
    : undefined;

  const banners = useMemo(() => {
    if (!countryCode) return [];

    const useCases: string[] = countryPermissions
      ? Array.from(countryPermissions.keys())
      : [];

    const translationKey = new TranslationKey(countryCode);
    const matches: BannerMatch[] = [];

    // Country-level banners (e.g., aic_br_listing_banner_warning_header)
    BANNER_VARIANTS.forEach((variant) => {
      const header = translationKey.translateValue(
        `listing_banner_${variant}_header`,
        t
      );
      const body = translationKey.translateValue(
        `listing_banner_${variant}_body`,
        t
      );

      if (header && body) {
        matches.push({ header, body, variant });
      }
    });

    // Use-case-specific banners based on user permissions
    useCases.forEach((useCase) => {
      const normalizedUseCase = normalizeStringForTranslation(useCase);

      BANNER_VARIANTS.forEach((variant) => {
        const header = translationKey.translateValue(
          `${normalizedUseCase}_listing_banner_${variant}_header`,
          t
        );
        const body = translationKey.translateValue(
          `${normalizedUseCase}_listing_banner_${variant}_body`,
          t
        );

        if (header && body) {
          matches.push({ header, body, variant });
        }
      });
    });

    return matches;
  }, [countryCode, countryPermissions, t]);

  if (banners.length === 0) return <></>;

  return (
    <>
      {banners.map(({ header, body, variant }) => (
        <div key={header} className={classnames(styles.padding_10)}>
          <KatAlert persistent header={header} variant={variant}>
            <Markdown>{body}</Markdown>
          </KatAlert>
        </div>
      ))}
    </>
  );
};

export default DocumentTypeWarningMessage;
